import { useEffect, useState } from "react";
import { Check, Copy, X } from "lucide-react";

import { Button } from "@/components/ui/button";

interface InstallSnippetProps {
  serviceId: string;
}

export function InstallSnippet({ serviceId }: InstallSnippetProps) {
  const [copyStatus, setCopyStatus] = useState<"idle" | "copied" | "failed">("idle");

  const installCode = `<script src="https://cdn.appio.so/v1/appio.js"></script>

<script>
  const appio = Appio({
    service: "${serviceId}"
  })

  function mobileApp() {
    appio.open({
      user: "YOUR_USER_HASH_HERE"
    })
  }
</script>

<a href="#" onclick="mobileApp()">Mobile APP</a>`;

  useEffect(() => {
    if (copyStatus === "idle") return;
    const timeout = setTimeout(() => setCopyStatus("idle"), 2000);
    return () => clearTimeout(timeout);
  }, [copyStatus]);

  const handleCopy = () => {
    navigator.clipboard.writeText(installCode).then(
      () => setCopyStatus("copied"),
      () => setCopyStatus("failed")
    );
  };

  return (
    <div className="overflow-hidden rounded-lg bg-zinc-950 ring-1 ring-black/5">
      <div className="flex items-center justify-between border-b border-white/10 py-1.5 pr-1.5 pl-4">
        <span className="font-mono text-xs text-zinc-400">HTML</span>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleCopy}
          className="text-zinc-400 hover:bg-white/10 hover:text-zinc-100"
        >
          {copyStatus === "copied" ? (
            <>
              <Check />
              Copied
            </>
          ) : copyStatus === "failed" ? (
            <>
              <X />
              Copy failed
            </>
          ) : (
            <>
              <Copy />
              Copy
            </>
          )}
        </Button>
      </div>
      <pre className="overflow-x-auto p-4 font-mono text-[13px] leading-relaxed text-zinc-100">
        <code>
          <span className="text-pink-400">&lt;script</span>
          <span className="text-sky-300"> src</span>
          <span className="text-zinc-400">=</span>
          <span className="text-amber-300">&quot;https://cdn.appio.so/v1/appio.js&quot;</span>
          <span className="text-pink-400">&gt;&lt;/script&gt;</span>
          {"\n\n"}
          <span className="text-pink-400">&lt;script&gt;</span>
          {"\n"}
          {"  "}<span className="text-violet-400">const</span>
          <span className="text-zinc-100"> appio </span>
          <span className="text-zinc-400">=</span>
          <span className="text-amber-300"> Appio</span>
          <span className="text-zinc-400">({"{"}</span>
          {"\n"}
          {"    "}<span className="text-sky-300">service</span>
          <span className="text-zinc-400">:</span>
          <span className="text-emerald-400"> &quot;{serviceId}&quot;</span>
          {"\n"}
          {"  "}<span className="text-zinc-400">{"}"})</span>
          {"\n\n"}
          {"  "}<span className="text-violet-400">function</span>
          <span className="text-amber-300"> mobileApp</span>
          <span className="text-zinc-400">() {"{"}</span>
          {"\n"}
          {"    "}<span className="text-zinc-100">appio</span>
          <span className="text-zinc-400">.</span>
          <span className="text-amber-300">open</span>
          <span className="text-zinc-400">({"{"}</span>
          {"\n"}
          {"      "}<span className="text-sky-300">user</span>
          <span className="text-zinc-400">:</span>
          <span className="text-emerald-400"> &quot;YOUR_USER_HASH_HERE&quot;</span>
          {"\n"}
          {"    "}<span className="text-zinc-400">{"}"})</span>
          {"\n"}
          {"  "}<span className="text-zinc-400">{"}"}</span>
          {"\n"}
          <span className="text-pink-400">&lt;/script&gt;</span>
          {"\n\n"}
          <span className="text-pink-400">&lt;a</span>
          <span className="text-sky-300"> href</span>
          <span className="text-zinc-400">=</span>
          <span className="text-amber-300">&quot;#&quot;</span>
          <span className="text-sky-300"> onclick</span>
          <span className="text-zinc-400">=</span>
          <span className="text-amber-300">&quot;mobileApp()&quot;</span>
          <span className="text-pink-400">&gt;</span>
          <span className="text-zinc-100">Mobile APP</span>
          <span className="text-pink-400">&lt;/a&gt;</span>
        </code>
      </pre>
    </div>
  );
}
